import Vue from 'vue';
import Vuex from 'vuex';
import { defaultApiCall } from '../utils/globalFunctions';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { resetAllStores } from './resetAllStores';
import { useActivityStore } from '@/store/activity';
Vue.use(Vuex);
// Simple storage helper functions
// Enhanced storage helper
const storage = {
  get(key) {
    try {
      const item = localStorage.getItem(key);
      if (item === null) return null;
      
      try {
        return JSON.parse(item);
      } catch {
        return item;
      }
    } catch (e) {
      console.error('Storage get error:', e);
      return null;
    }
  },
  set(key, value) {
    try {
      const toStore = typeof value === 'object' ? JSON.stringify(value) : value;
      localStorage.setItem(key, toStore);
    } catch (e) {
      console.error('Storage set error:', e);
    }
  },
  remove(key) {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.error('Storage remove error:', e);
    }
  }
};

const store = new Vuex.Store({
  state: {
    installPromptAvailable: false,
    appInstalled: false,
    reloadKey: 0,
    user: storage.get('user'),
    token: storage.get('token'),
    pushId: storage.get('oneSignalUserId'),
    lastSyncedPushId: storage.get('lastSyncedPushId'),
    notificationPermission: getNotificationPermission(),
    deviceIdentifier: storage.get('deviceIdentifier'),
  },
  mutations: {
    setInstallPromptAvailable(state, value) {
      state.installPromptAvailable = value;
    },
    setAppInstalled(state, value) {
      state.appInstalled = value;
    },
    setNotificationPermission(state, permission) {
      state.notificationPermission = permission;
    },
    incrementReloadKey(state) {
      state.reloadKey += 1;
    },
    setUser(state, user) {
      state.user = user;
      storage.set('user', user);
    },
    setToken(state, token) {
      if (token) validateTokenFormat(token);
      state.token = token;
      storage.set('token', token); // Will store as raw string
    },
    clearAuth(state) {
      state.user = null;
      state.token = null;
      state.pushId = null;
      ['user', 'token', 'oneSignalUserId'].forEach(key => storage.remove(key));
    },
    setPushId(state, id) {
      state.pushId = id;
      storage.set('oneSignalUserId', id);
    },
    setLastSyncedPushId(state, id) {
      state.lastSyncedPushId = id;
      storage.set('lastSyncedPushId', id);
    },
    setDeviceIdentifier(state, device) {
      state.deviceIdentifier = device;
      storage.set('deviceIdentifier', device);
    },
    // Add these new mutations for partial updates
    updateUserField(state, { field, value }) {
      if (state.user) {
        Vue.set(state.user, field, value);
        storage.set('user', state.user);
      }
    },
    updateUserImage(state, { image_name, image_url }) {
      if (state.user) {
        Vue.set(state.user, 'image_name', image_name);
        Vue.set(state.user, 'image_url', image_url);
        storage.set('user', state.user);
      }
    },
    clearUserImage(state) {
      if (state.user) {
        Vue.set(state.user, 'image_name', null);
        Vue.set(state.user, 'image_url', null);
        storage.set('user', state.user);
      }
    },
  },
  actions: {
    setInstallPromptAvailable({ commit }, value) {
      commit('setInstallPromptAvailable', value);
    },
    setAppInstalled({ commit }, value) {
      commit('setAppInstalled', value);
    },
    async checkNotificationPermission({ commit }) {
      try {
        let permission = 'default';
        if (Capacitor.isNativePlatform()) {
          const permStatus = await PushNotifications.requestPermissions();
          permission = permStatus.receive === 'granted' ? 'granted' : 'denied';
        } else {
          permission = await Notification.requestPermission();
        }
        commit('setNotificationPermission', permission);
        return permission;
      } catch (error) {
        console.error('Error checking notification permission:', error);
        commit('setNotificationPermission', 'default');
        return 'default';
      }
    },
    async login({ commit }, payload) {
      try {
        if (!payload?.token) throw new Error('Invalid login payload');
        
        commit('setUser', payload.user);
        commit('setToken', payload.token);
        useActivityStore().addActivity('LOGIN_SUCCESS');
        return true;
      } catch (error) {
        console.error('Login failed:', error);
        commit('clearAuth');
        throw error;
      }
    },
    logout({ commit }) {
      resetAllStores();
      commit('clearAuth');
      useActivityStore().addActivity('LOGOUT');
    },
    pushId({ commit }, id) {
      commit('setPushId', id);
    },
    deviceIdentifier({ commit }, device) {
      commit('setDeviceIdentifier', device);
    },
    async syncUserIdWithServer({ state, commit }) {
      try {
        if (!state.token || !state.pushId || state.pushId === state.lastSyncedPushId) {
          return;
        }

        const response = await withTimeout(
          defaultApiCall({
            method: 'POST',
            url: 'App/updateToken',
            headers: {
              Authorization: `Bearer ${state.token}`,
              'Content-Type': 'application/json'
            },
            data: { pushId: state.pushId }
          }),
          10000 // 10 second timeout
        );

        if (response.status === 'error') {
          throw new Error(response.message || 'Failed to update token');
        }

        commit('setLastSyncedPushId', state.pushId);
      } catch (error) {
        console.error('Error syncing push ID:', error);
        throw error;
      }
    },
    // Enhanced user action that can handle partial updates
    async updateUser({ commit, state }, payload) {
      if (!state.user) return;
      
      // If payload is a complete user object (legacy support)
      if (payload.user) {
        commit('setUser', payload.user);
      } 
      // If payload is a partial update
      else {
        const updatedUser = { ...state.user, ...payload };
        commit('setUser', updatedUser);
      }
    },
  },
  getters: {
    isLoggedIn: (state) => !!state.token,
    getToken: (state) => state.token,
    getUser: (state) => state.user,
    getPushId: (state) => state.pushId,
    getLastSyncedPushId: (state) => state.lastSyncedPushId,
    getNotificationPermission: (state) => state.notificationPermission,
    getDeviceIdentifier: (state) => state.deviceIdentifier,
  }
});

function getNotificationPermission() {
  try {
    return typeof Notification !== 'undefined' ? Notification.permission : 'default';
  } catch {
    return 'default';
  }
}

function validateTokenFormat(token) {
  if (typeof token !== 'string') {
    throw new Error('Invalid token format: must be a string');
  }
  // Additional JWT validation if needed
  if (token.split('.').length !== 3) {
    console.warn('Token format warning: Expected JWT format');
  }
}

function withTimeout(promise, timeout) {
  const timeoutPromise = new Promise((_, reject) => 
    setTimeout(() => reject(new Error('Request timeout')), timeout)
  );
  return Promise.race([promise, timeoutPromise]);
}

export default store;